import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy,
  addDoc, 
  updateDoc, 
  doc, 
  deleteDoc,
  getDocs,
  onSnapshot,
  limit as firestoreLimit,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useAuth } from './AuthContext';
import { notificationModel } from '../data/notificationModel';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load notifications where user is the recipient
  const loadUserNotifications = () => {
    if (!currentUser) return () => {};

    setLoading(true);
    setError(null);

    console.log('🔔 NotificationContext: Loading notifications for userId:', currentUser.uid);

    const notificationsRef = collection(db, notificationModel.collection);
    
    // Try with index first, fallback to simple query
    let q;
    try {
      q = query(
        notificationsRef,
        where('userId', '==', currentUser.uid),
        orderBy('read', 'asc'),
        orderBy('createdAt', 'desc'),
        firestoreLimit(50)
      );
    } catch (error) {
      // Fallback: query without orderBy
      console.warn('⚠️ NotificationContext: Index missing, using fallback query');
      q = query(
        notificationsRef,
        where('userId', '==', currentUser.uid),
        firestoreLimit(50)
      );
    }

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        console.log('🔔 NotificationContext: Received snapshot:', {
          size: snapshot.size,
          userId: currentUser.uid
        });
        
        // Debug: Log all notifications in snapshot
        console.log('🔔 Total notifications in snapshot:', snapshot.size);
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          const isVideoCallRequest = data.type === 'video_call_request';
          console.log('🔔 Notification in snapshot:', {
            id: doc.id,
            userId: data.userId,
            type: data.type,
            isVideoCallRequest: isVideoCallRequest,
            title: data.title,
            read: data.read,
            readType: typeof data.read,
            readValue: data.read === false ? 'FALSE' : data.read === true ? 'TRUE' : 'OTHER',
            createdAt: data.createdAt,
            matchesCurrentUser: data.userId === currentUser.uid ? '✅ MATCH' : '❌ NO MATCH'
          });
        });
        
        const notificationsData = snapshot.docs
          .map((doc, index) => {
            const data = doc.data();
            console.log(`🔔 Mapping notification ${index + 1}/${snapshot.size}:`, {
              docId: doc.id,
              dataId: data.id,
              type: data.type,
              read: data.read,
              readType: typeof data.read,
              hasId: !!doc.id,
              dataKeys: Object.keys(data)
            });
            
            // CRITICAL FIX: Extract id from data first, then override with doc.id
            // This ensures doc.id is always used, even if data.id exists
            const { id: dataId, ...dataWithoutId } = data;
            
            const notification = {
              ...dataWithoutId, // Spread data without id first
              id: doc.id, // Then set id from doc.id (this will override any data.id)
              _id: doc.id // Keep _id as backup
            };
            
            // Debug: Log each notification after mapping
            if (data.type === 'video_call_request') {
              console.log('🔔 Video Call Request Notification (after mapping):', {
                id: notification.id,
                docId: doc.id,
                dataId: dataId,
                type: notification.type,
                read: notification.read,
                readType: typeof notification.read,
                readValue: notification.read === false ? 'FALSE' : notification.read === true ? 'TRUE' : 'OTHER',
                willBeCountedAsUnread: !notification.read,
                hasId: !!notification.id,
                notificationKeys: Object.keys(notification)
              });
            }
            
            return notification;
          })
          .filter(notif => {
            const hasId = !!notif.id && notif.id !== '';
            if (!hasId) {
              console.warn('⚠️ Filtering out notification without ID:', {
                id: notif.id,
                type: notif.type,
                _id: notif._id
              });
            }
            return hasId;
          });
        
        console.log('🔔 After mapping and filtering:', {
          total: notificationsData.length,
          types: notificationsData.map(n => n.type),
          videoCallRequests: notificationsData.filter(n => n.type === 'video_call_request').length
        });
        
        // Debug: Check unread count calculation
        // Log each notification before filtering
        console.log('🔔 Before filtering - All notifications:', notificationsData.map(n => ({
          id: n.id,
          type: n.type,
          read: n.read,
          readType: typeof n.read,
          readValue: n.read === false ? 'FALSE' : n.read === true ? 'TRUE' : 'OTHER',
          notRead: !n.read,
          notReadType: typeof !n.read,
          notReadValue: !n.read === true ? 'TRUE' : !n.read === false ? 'FALSE' : 'OTHER'
        })));
        
        const unreadNotifications = notificationsData.filter(n => !n.read);
        
        const videoCallRequestNotifications = notificationsData.filter(n => n.type === 'video_call_request');
        
        const unreadVideoCallRequests = videoCallRequestNotifications.filter(n => !n.read);
        
        console.log('🔔 NotificationContext: Processed notifications:', {
          total: notificationsData.length,
          unread: unreadNotifications.length,
          unreadDetails: unreadNotifications.map(n => ({
            id: n.id,
            type: n.type,
            read: n.read,
            readType: typeof n.read,
            readValue: n.read === false ? 'FALSE' : n.read === true ? 'TRUE' : 'OTHER'
          })),
          types: notificationsData.map(n => n.type),
          videoCallRequests: videoCallRequestNotifications.length,
          unreadVideoCallRequests: unreadVideoCallRequests.length,
          unreadVideoCallRequestDetails: unreadVideoCallRequests.map(n => ({
            id: n.id,
            type: n.type,
            read: n.read,
            readType: typeof n.read,
            readValue: n.read === false ? 'FALSE' : n.read === true ? 'TRUE' : 'OTHER'
          }))
        });
        
        // Sort manually if needed
        notificationsData.sort((a, b) => {
          // Unread first
          if (a.read !== b.read) {
            return a.read ? 1 : -1;
          }
          // Then by date
          const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
          const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
          return dateB - dateA;
        });
        
        setNotifications(notificationsData);
        const unread = notificationsData.filter(n => !n.read).length;
        setUnreadCount(unread);
        console.log('🔔 NotificationContext: Set unread count:', unread);
        setLoading(false);
      },
      (error) => {
        console.error('❌ NotificationContext: Error loading notifications:', error);
        console.error('Error details:', {
          userId: currentUser.uid,
          errorCode: error.code,
          errorMessage: error.message
        });
        setError('เกิดข้อผิดพลาดในการโหลดการแจ้งเตือน');
        setLoading(false);
      }
    );

    return unsubscribe;
  };

  // Load notifications for current user
  useEffect(() => {
    if (!currentUser) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    const unsubscribe = loadUserNotifications();
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Create a new notification
  const createNotification = async (userId, type, title, message, data = {}) => {
    try {
      setLoading(true);
      setError(null);

      // Create notification with Firestore Timestamp
      const now = Timestamp.now();
      const notification = {
        userId,
        type,
        title,
        message,
        data,
        read: false,
        readAt: null,
        createdAt: now,
        updatedAt: now
      };

      console.log('Creating notification:', {
        userId,
        type,
        title,
        message,
        data
      });

      const docRef = await addDoc(collection(db, notificationModel.collection), notification);
      console.log('Notification created with ID:', docRef.id);
      
      return true;
    } catch (error) {
      console.error('Error creating notification:', error);
      console.error('Error details:', {
        userId,
        type,
        title,
        message,
        errorCode: error.code,
        errorMessage: error.message,
        errorStack: error.stack
      });
      setError('เกิดข้อผิดพลาดในการสร้างการแจ้งเตือน');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    if (!currentUser) {
      console.warn('markAsRead: No current user');
      return false;
    }
    
    if (!notificationId) {
      console.error('markAsRead: Notification ID is required but was:', notificationId);
      return false;
    }

    // Validate notificationId is a string and not empty
    if (typeof notificationId !== 'string' || notificationId.trim() === '') {
      console.error('markAsRead: Invalid notification ID:', notificationId, typeof notificationId);
      return false;
    }

    // Validate collection name
    const collectionName = notificationModel.collection || 'notifications';
    if (!collectionName || typeof collectionName !== 'string') {
      console.error('markAsRead: Invalid collection name:', collectionName);
      return false;
    }

    try {
      setLoading(true);
      setError(null);

      // Ensure we have a valid document reference
      console.log(`markAsRead: Creating doc reference with collection="${collectionName}", id="${notificationId}"`);
      const notificationRef = doc(db, collectionName, notificationId);
      
      // Verify the document reference is valid
      if (!notificationRef) {
        console.error('markAsRead: Failed to create document reference');
        return false;
      }

      if (!notificationRef.path || notificationRef.path.split('/').length !== 2) {
        console.error('markAsRead: Invalid document path:', notificationRef.path);
        return false;
      }

      console.log(`markAsRead: Updating document at path: ${notificationRef.path}`);
      await updateDoc(notificationRef, {
        read: true,
        readAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      
      console.log('markAsRead: Successfully marked as read');
      
      // Optimistically update local state immediately
      setNotifications(prev => {
        const updated = prev.map(n => 
          (n.id === notificationId || n._id === notificationId) ? { ...n, read: true } : n
        );
        // Recalculate unread count
        const newUnreadCount = updated.filter(n => !n.read).length;
        setUnreadCount(newUnreadCount);
        console.log('markAsRead: Updated unread count to:', newUnreadCount);
        return updated;
      });
      
      return true;
    } catch (error) {
      console.error('markAsRead: Error marking notification as read:', error);
      console.error('markAsRead: Notification ID was:', notificationId);
      console.error('markAsRead: Collection name:', collectionName);
      console.error('markAsRead: Error code:', error.code);
      console.error('markAsRead: Error message:', error.message);
      
      // Don't throw error, just log it so navigation can continue
      setError('เกิดข้อผิดพลาดในการอัปเดตการแจ้งเตือน');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!currentUser) {
      console.warn('markAllAsRead: No current user');
      return false;
    }

    try {
      setLoading(true);
      setError(null);

      const unreadNotifications = notifications.filter(n => !n.read);
      
      if (unreadNotifications.length === 0) {
        console.log('markAllAsRead: No unread notifications to mark');
        return true;
      }

      console.log(`markAllAsRead: Marking ${unreadNotifications.length} notifications as read`);
      
      const updatePromises = unreadNotifications.map(notification => {
        const notificationId = notification.id || notification._id;
        if (!notificationId) {
          console.warn('markAllAsRead: Notification missing ID:', notification);
          return Promise.resolve();
        }
        
        const notificationRef = doc(db, notificationModel.collection, notificationId);
        return updateDoc(notificationRef, {
          read: true,
          readAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        }).catch(error => {
          console.error(`markAllAsRead: Error updating notification ${notificationId}:`, error);
          // Continue with other updates even if one fails
          return null;
        });
      });

      await Promise.all(updatePromises);
      
      console.log('markAllAsRead: Successfully marked all notifications as read');
      
      // Optimistically update local state immediately
      setNotifications(prev => {
        const updated = prev.map(n => ({ ...n, read: true }));
        setUnreadCount(0);
        console.log('markAllAsRead: Updated all notifications to read, unread count: 0');
        return updated;
      });
      
      return true;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      setError('เกิดข้อผิดพลาดในการอัปเดตการแจ้งเตือน');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Get equipment-related notification count based on user role
  const getEquipmentNotificationCount = (userRole) => {
    if (!notifications || notifications.length === 0) {
      console.log('🔔 getEquipmentNotificationCount: No notifications available');
      return 0;
    }
    
    const equipmentNotificationTypes = {
      // For Leaders/Deputy Leaders: notifications that require their action
      LEADER: [
        'inspection_submitted',
        'inspection_updated',
        'equipment_borrow_requested',
        'equipment_return_requested',
        'equipment_repair_requested',
        'equipment_repair_receive_requested',
        'equipment_repair_completion_requested'
      ],
      DEPUTY_LEADER: [
        'inspection_submitted',
        'inspection_updated',
        'equipment_borrow_requested',
        'equipment_return_requested',
        'equipment_repair_requested',
        'equipment_repair_receive_requested',
        'equipment_repair_completion_requested'
      ],
      // For Members: notifications about their requests being processed
      MEMBER: [
        'inspection_approved',
        'inspection_rejected',
        'equipment_borrow_approved',
        'equipment_borrow_rejected',
        'equipment_return_approved',
        'equipment_return_rejected',
        'equipment_repair_approved',
        'equipment_repair_rejected',
        'equipment_repair_receive_approved',
        'equipment_repair_receive_rejected',
        'equipment_repair_completed',
        'equipment_repair_completion_requested'
      ]
    };
    
    const relevantTypes = equipmentNotificationTypes[userRole] || equipmentNotificationTypes.MEMBER;
    
    // Debug: Log all notification types
    const allNotificationTypes = [...new Set(notifications.map(n => n.type))];
    const unreadNotifications = notifications.filter(n => !n.read);
    const unreadTypes = [...new Set(unreadNotifications.map(n => n.type))];
    
    const unreadEquipmentNotifications = notifications.filter(notif => 
      !notif.read && relevantTypes.includes(notif.type)
    );
    
    // Also check read notifications for debugging
    const readEquipmentNotifications = notifications.filter(notif => 
      notif.read && relevantTypes.includes(notif.type)
    );
    
    console.log('🔔 getEquipmentNotificationCount:', {
      userRole,
      totalNotifications: notifications.length,
      relevantTypes,
      allNotificationTypes,
      unreadNotificationsCount: unreadNotifications.length,
      unreadTypes,
      unreadEquipmentNotifications: unreadEquipmentNotifications.length,
      unreadEquipmentDetails: unreadEquipmentNotifications.map(n => ({ 
        type: n.type, 
        read: n.read,
        title: n.title?.substring(0, 30)
      })),
      readEquipmentNotificationsCount: readEquipmentNotifications.length,
      readEquipmentDetails: readEquipmentNotifications.slice(0, 5).map(n => ({ 
        type: n.type, 
        read: n.read,
        title: n.title?.substring(0, 30)
      }))
    });
    
    return unreadEquipmentNotifications.length;
  };

  // Get equipment-related pending actions count (to-do list) based on user role
  // This counts actual pending actions from Firestore, not just unread notifications
  const getEquipmentPendingActionsCount = async (userRole, teamId, userId) => {
    if (!teamId || !userId) return 0;

    try {
      let totalCount = 0;

      if (userRole === 'LEADER' || userRole === 'DEPUTY_LEADER') {
        // For Leaders: Count pending actions that need their approval
        
        // 0. Pending equipment approvals (อุปกรณ์ที่รออนุมัติ)
        try {
          const equipmentQuery = query(
            collection(db, 'equipment'),
            where('teamId', '==', teamId),
            where('status', '==', 'PENDING_APPROVAL')
          );
          const equipmentSnapshot = await getDocs(equipmentQuery);
          totalCount += equipmentSnapshot.size;
          console.log('📋 getEquipmentPendingActionsCount: Pending equipment approvals:', equipmentSnapshot.size);
        } catch (err) {
          console.error('Error counting pending equipment approvals:', err);
        }

        // 1. Pending inspections
        const inspectionsQuery = query(
          collection(db, 'equipmentInspections'),
          where('teamId', '==', teamId),
          where('status', '==', 'PENDING_APPROVAL')
        );
        const inspectionsSnapshot = await getDocs(inspectionsQuery);
        totalCount += inspectionsSnapshot.size;

        // 2. Pending borrow requests
        const borrowsQuery = query(
          collection(db, 'equipmentBorrows'),
          where('teamId', '==', teamId),
          where('status', '==', 'PENDING')
        );
        const borrowsSnapshot = await getDocs(borrowsQuery);
        totalCount += borrowsSnapshot.size;

        // 3. Pending return requests
        const returnsQuery = query(
          collection(db, 'equipmentBorrows'),
          where('teamId', '==', teamId),
          where('returnStatus', '==', 'PENDING')
        );
        const returnsSnapshot = await getDocs(returnsQuery);
        totalCount += returnsSnapshot.size;

        // 4. Pending repair requests
        const repairsQuery = query(
          collection(db, 'equipmentRepairs'),
          where('teamId', '==', teamId),
          where('status', '==', 'PENDING')
        );
        const repairsSnapshot = await getDocs(repairsQuery);
        totalCount += repairsSnapshot.size;

        // 5. Pending repair receive requests
        const repairReceivesQuery = query(
          collection(db, 'equipmentRepairs'),
          where('teamId', '==', teamId),
          where('receiveStatus', '==', 'PENDING')
        );
        const repairReceivesSnapshot = await getDocs(repairReceivesQuery);
        totalCount += repairReceivesSnapshot.size;

        // 6. Pending repair completion requests
        const repairCompletionsQuery = query(
          collection(db, 'equipmentRepairs'),
          where('teamId', '==', teamId),
          where('completionRequestStatus', '==', 'PENDING')
        );
        const repairCompletionsSnapshot = await getDocs(repairCompletionsQuery);
        totalCount += repairCompletionsSnapshot.size;

      } else {
        // For Members: Count their own pending requests
        
        // 1. Their inspections pending approval
        const inspectionsQuery = query(
          collection(db, 'equipmentInspections'),
          where('teamId', '==', teamId),
          where('inspectedBy', '==', userId),
          where('status', '==', 'PENDING_APPROVAL')
        );
        const inspectionsSnapshot = await getDocs(inspectionsQuery);
        totalCount += inspectionsSnapshot.size;

        // 2. Their borrow requests pending approval
        const borrowsQuery = query(
          collection(db, 'equipmentBorrows'),
          where('teamId', '==', teamId),
          where('borrowedBy', '==', userId),
          where('status', '==', 'PENDING')
        );
        const borrowsSnapshot = await getDocs(borrowsQuery);
        totalCount += borrowsSnapshot.size;

        // 3. Their return requests pending approval
        const returnsQuery = query(
          collection(db, 'equipmentBorrows'),
          where('teamId', '==', teamId),
          where('borrowedBy', '==', userId),
          where('returnStatus', '==', 'PENDING')
        );
        const returnsSnapshot = await getDocs(returnsQuery);
        totalCount += returnsSnapshot.size;

        // 4. Their repair requests pending approval
        const repairsQuery = query(
          collection(db, 'equipmentRepairs'),
          where('teamId', '==', teamId),
          where('reportedBy', '==', userId),
          where('status', '==', 'PENDING')
        );
        const repairsSnapshot = await getDocs(repairsQuery);
        totalCount += repairsSnapshot.size;

        // 5. Their repair completion requests pending approval
        const repairCompletionsQuery = query(
          collection(db, 'equipmentRepairs'),
          where('teamId', '==', teamId),
          where('reportedBy', '==', userId),
          where('completionRequestStatus', '==', 'PENDING')
        );
        const repairCompletionsSnapshot = await getDocs(repairCompletionsQuery);
        totalCount += repairCompletionsSnapshot.size;
      }

      console.log('📋 getEquipmentPendingActionsCount:', {
        userRole,
        teamId,
        userId,
        totalCount
      });

      return totalCount;
    } catch (err) {
      console.error('Error getting equipment pending actions count:', err);
      return 0;
    }
  };

  // Delete a notification
  const deleteNotification = async (notificationId) => {
    if (!currentUser) {
      console.warn('deleteNotification: No current user');
      return false;
    }
    
    if (!notificationId) {
      console.error('deleteNotification: Notification ID is required but was:', notificationId);
      return false;
    }

    // Validate notificationId is a string and not empty
    if (typeof notificationId !== 'string' || notificationId.trim() === '') {
      console.error('deleteNotification: Invalid notification ID:', notificationId);
      return false;
    }

    // Validate collection name
    const collectionName = notificationModel.collection || 'notifications';
    if (!collectionName || typeof collectionName !== 'string') {
      console.error('deleteNotification: Invalid collection name:', collectionName);
      return false;
    }

    try {
      setLoading(true);
      setError(null);

      console.log(`deleteNotification: Creating doc reference with collection="${collectionName}", id="${notificationId}"`);
      const notificationRef = doc(db, collectionName, notificationId);
      
      if (!notificationRef || !notificationRef.path || notificationRef.path.split('/').length !== 2) {
        console.error('deleteNotification: Invalid document reference');
        return false;
      }

      console.log(`deleteNotification: Deleting document at path: ${notificationRef.path}`);
      await deleteDoc(notificationRef);
      
      console.log('deleteNotification: Successfully deleted');
      return true;
    } catch (error) {
      console.error('deleteNotification: Error deleting notification:', error);
      console.error('deleteNotification: Notification ID was:', notificationId);
      console.error('deleteNotification: Collection name:', collectionName);
      setError('เกิดข้อผิดพลาดในการลบการแจ้งเตือน');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Delete all read notifications
  const deleteAllRead = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      setError(null);

      const readNotifications = notifications.filter(n => n.read);
      const deletePromises = readNotifications.map(notification => {
        return deleteDoc(doc(db, notificationModel.collection, notification.id));
      });

      await Promise.all(deletePromises);
      
      return true;
    } catch (error) {
      console.error('Error deleting read notifications:', error);
      setError('เกิดข้อผิดพลาดในการลบการแจ้งเตือน');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    notifications,
    unreadCount,
    loading,
    error,
    createNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllRead,
    getEquipmentNotificationCount,
    getEquipmentPendingActionsCount
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

